/**
 * Weather File Search Service
 *
 * Provides searching, filtering, and sorting capabilities for weather files.
 * Weather data is loaded asynchronously from JSON files.
 */

import type {
  WeatherFileMetadata,
  WeatherFileSearchQuery,
  WeatherFileSearchResult,
  ASHRAEClimateZone,
  WeatherFileSourceType,
} from '../types';
import {
  loadWeatherManifest,
  loadCountryWeatherFiles,
  type WeatherIndexManifest,
} from '../data/weatherFileIndex';

/**
 * Calculate the Haversine distance between two geographic points.
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Simple fuzzy text matching score.
 * @param text - Text to search in
 * @param query - Query to search for
 * @returns Score between 0 and 1
 */
function fuzzyMatch(text: string, query: string): number {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Exact match
  if (lowerText === lowerQuery) return 1;

  // Contains match
  if (lowerText.includes(lowerQuery)) {
    return 0.8 + (0.2 * lowerQuery.length) / lowerText.length;
  }

  // Word-based matching
  const queryWords = lowerQuery.split(/\s+/);
  const textWords = lowerText.split(/\s+/);
  let matchedWords = 0;

  for (const qWord of queryWords) {
    for (const tWord of textWords) {
      if (tWord.includes(qWord) || qWord.includes(tWord)) {
        matchedWords++;
        break;
      }
    }
  }

  if (matchedWords > 0) {
    return 0.5 * (matchedWords / queryWords.length);
  }

  return 0;
}

/**
 * Search weather files with text matching.
 * @param query - Text query
 * @param files - Files to search
 * @returns Files with relevance scores
 */
function textSearch(
  query: string,
  files: WeatherFileMetadata[]
): WeatherFileSearchResult[] {
  const results: WeatherFileSearchResult[] = [];

  for (const file of files) {
    // Search across multiple fields
    const cityScore = fuzzyMatch(file.location.city, query);
    const regionScore = fuzzyMatch(file.location.region, query);
    const countryScore = fuzzyMatch(file.location.country, query);
    const nameScore = fuzzyMatch(file.name, query);

    // Take the highest score
    const maxScore = Math.max(cityScore, regionScore, countryScore, nameScore);

    if (maxScore > 0.3) {
      results.push({
        ...file,
        score: maxScore,
      });
    }
  }

  return results;
}

/**
 * Filter weather files by country code.
 */
function filterByCountry(
  files: WeatherFileMetadata[],
  countryCode: string
): WeatherFileMetadata[] {
  const upperCode = countryCode.toUpperCase();
  return files.filter((f) => f.location.countryCode.toUpperCase() === upperCode);
}

/**
 * Filter weather files by climate zone.
 */
function filterByClimateZone(
  files: WeatherFileMetadata[],
  zones: ASHRAEClimateZone | ASHRAEClimateZone[]
): WeatherFileMetadata[] {
  const zoneArray = Array.isArray(zones) ? zones : [zones];
  return files.filter((f) => {
    if (!f.climateZone.ashrae) return false;
    return zoneArray.includes(f.climateZone.ashrae);
  });
}

/**
 * Filter weather files by source type.
 */
function filterBySourceType(
  files: WeatherFileMetadata[],
  types: WeatherFileSourceType | WeatherFileSourceType[]
): WeatherFileMetadata[] {
  const typeArray = Array.isArray(types) ? types : [types];
  return files.filter((f) => typeArray.includes(f.source.type));
}

/**
 * Filter weather files by year range.
 */
function filterByYearRange(
  files: WeatherFileMetadata[],
  yearRanges: string | string[]
): WeatherFileMetadata[] {
  const rangeArray = Array.isArray(yearRanges) ? yearRanges : [yearRanges];
  return files.filter((f) => {
    const years = f.source.years;
    if (!years) return rangeArray.includes('none'); // Allow filtering for files without year range
    return rangeArray.includes(years);
  });
}

/**
 * Filter weather files by geographic radius.
 */
function filterByRadius(
  files: WeatherFileMetadata[],
  centerLat: number,
  centerLon: number,
  radiusKm: number
): WeatherFileSearchResult[] {
  return files
    .map((file) => ({
      ...file,
      distance: calculateDistance(
        centerLat,
        centerLon,
        file.location.latitude,
        file.location.longitude
      ),
    }))
    .filter((f) => f.distance <= radiusKm);
}

/**
 * Filter weather files by bounding box.
 */
function filterByBounds(
  files: WeatherFileMetadata[],
  bounds: { north: number; south: number; east: number; west: number }
): WeatherFileMetadata[] {
  return files.filter((f) => {
    const lat = f.location.latitude;
    const lon = f.location.longitude;
    return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
  });
}

/**
 * Filter weather files by elevation range.
 */
function filterByElevation(
  files: WeatherFileMetadata[],
  minElevation?: number,
  maxElevation?: number
): WeatherFileMetadata[] {
  return files.filter((f) => {
    if (minElevation !== undefined && f.location.elevation < minElevation) {
      return false;
    }
    if (maxElevation !== undefined && f.location.elevation > maxElevation) {
      return false;
    }
    return true;
  });
}

/**
 * Sort weather files by specified criteria.
 */
function sortResults(
  files: WeatherFileSearchResult[],
  sortBy: 'distance' | 'name' | 'popularity' | 'elevation',
  sortOrder: 'asc' | 'desc'
): WeatherFileSearchResult[] {
  const sorted = [...files].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'distance':
        comparison = (a.distance ?? Infinity) - (b.distance ?? Infinity);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'popularity':
        comparison = (b.popularity ?? 0) - (a.popularity ?? 0);
        break;
      case 'elevation':
        comparison = a.location.elevation - b.location.elevation;
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  return sorted;
}

/**
 * Search and filter weather files (async version).
 * When searching with country filter, only loads that country's data.
 * For text search without country filter, loads all data (may be slow).
 *
 * @param query - Search query parameters
 * @param loadedFiles - Optional pre-loaded files to search (for performance)
 * @returns Filtered and sorted weather file results
 */
export async function searchWeatherFiles(
  query: WeatherFileSearchQuery,
  loadedFiles?: WeatherFileMetadata[]
): Promise<WeatherFileSearchResult[]> {
  let files: WeatherFileMetadata[];

  if (loadedFiles) {
    // Use pre-loaded files
    files = loadedFiles;
  } else if (query.countryCode) {
    // Load only the specific country
    files = await loadCountryWeatherFiles(query.countryCode);
  } else {
    // Need to load all countries for text search
    const manifest = await loadWeatherManifest();
    files = [];
    // Load in parallel for better performance, using allSettled for resilience
    const loadPromises = manifest.countries.map((c) => loadCountryWeatherFiles(c.code));
    const results = await Promise.allSettled(loadPromises);
    for (const result of results) {
      if (result.status === 'fulfilled') {
        files.push(...result.value);
      }
    }
  }

  let results: WeatherFileSearchResult[] = files.map((f) => ({ ...f }));

  // Text search
  if (query.text && query.text.trim().length > 0) {
    results = textSearch(query.text, results);
  }

  // Country filter (already filtered if we loaded a specific country)
  if (query.countryCode && loadedFiles) {
    results = filterByCountry(results, query.countryCode) as WeatherFileSearchResult[];
  }

  // Climate zone filter
  if (query.climateZone) {
    results = filterByClimateZone(results, query.climateZone) as WeatherFileSearchResult[];
  }

  // Source type filter
  if (query.sourceType) {
    results = filterBySourceType(results, query.sourceType) as WeatherFileSearchResult[];
  }

  // Year range filter
  if (query.yearRange) {
    results = filterByYearRange(results, query.yearRange) as WeatherFileSearchResult[];
  }

  // Geographic radius filter
  if (query.center && query.radiusKm) {
    results = filterByRadius(
      results,
      query.center.latitude,
      query.center.longitude,
      query.radiusKm
    );
  } else if (query.center) {
    // If center is provided without radius, calculate distances for sorting
    results = results.map((file) => ({
      ...file,
      distance: calculateDistance(
        query.center!.latitude,
        query.center!.longitude,
        file.location.latitude,
        file.location.longitude
      ),
    }));
  }

  // Bounding box filter
  if (query.bounds) {
    results = filterByBounds(results, query.bounds) as WeatherFileSearchResult[];
  }

  // Elevation filter
  if (query.minElevation !== undefined || query.maxElevation !== undefined) {
    results = filterByElevation(
      results,
      query.minElevation,
      query.maxElevation
    ) as WeatherFileSearchResult[];
  }

  // Sort results
  const sortBy = query.sortBy ?? (query.center ? 'distance' : 'popularity');
  const sortOrder = query.sortOrder ?? (sortBy === 'popularity' ? 'desc' : 'asc');
  results = sortResults(results, sortBy, sortOrder);

  // Pagination
  const offset = query.offset ?? 0;
  const limit = query.limit ?? 50;
  results = results.slice(offset, offset + limit);

  return results;
}

/**
 * Search result with total count for pagination.
 */
export interface SearchResultWithCount {
  results: WeatherFileSearchResult[];
  totalCount: number;
  hasMore: boolean;
}

/**
 * Filter facet counts for dynamic filter updates.
 */
export interface FilterFacetCounts {
  climateZones: { zone: string; count: number }[];
  sourceTypes: { type: string; count: number }[];
  yearRanges: { yearRange: string; count: number }[];
}

/**
 * Search result with total count and facet counts for dynamic filters.
 */
export interface SearchResultWithFacets extends SearchResultWithCount {
  facets: FilterFacetCounts;
}

/**
 * Search weather files and return results with total count.
 * This is useful for implementing pagination/infinite scroll.
 */
export async function searchWeatherFilesWithCount(
  query: WeatherFileSearchQuery,
  loadedFiles?: WeatherFileMetadata[]
): Promise<SearchResultWithCount> {
  let files: WeatherFileMetadata[];

  if (loadedFiles) {
    files = loadedFiles;
  } else if (query.countryCode) {
    files = await loadCountryWeatherFiles(query.countryCode);
  } else {
    const manifest = await loadWeatherManifest();
    files = [];
    const loadPromises = manifest.countries.map((c) => loadCountryWeatherFiles(c.code));
    const results = await Promise.allSettled(loadPromises);
    for (const result of results) {
      if (result.status === 'fulfilled') {
        files.push(...result.value);
      }
    }
  }

  let searchResults: WeatherFileSearchResult[] = files.map((f) => ({ ...f }));

  // Text search
  if (query.text && query.text.trim().length > 0) {
    searchResults = textSearch(query.text, searchResults);
  }

  // Country filter (already filtered if we loaded a specific country)
  if (query.countryCode && loadedFiles) {
    searchResults = filterByCountry(searchResults, query.countryCode) as WeatherFileSearchResult[];
  }

  // Climate zone filter
  if (query.climateZone) {
    searchResults = filterByClimateZone(searchResults, query.climateZone) as WeatherFileSearchResult[];
  }

  // Source type filter
  if (query.sourceType) {
    searchResults = filterBySourceType(searchResults, query.sourceType) as WeatherFileSearchResult[];
  }

  // Year range filter
  if (query.yearRange) {
    searchResults = filterByYearRange(searchResults, query.yearRange) as WeatherFileSearchResult[];
  }

  // Geographic radius filter
  if (query.center && query.radiusKm) {
    searchResults = filterByRadius(
      searchResults,
      query.center.latitude,
      query.center.longitude,
      query.radiusKm
    );
  } else if (query.center) {
    searchResults = searchResults.map((file) => ({
      ...file,
      distance: calculateDistance(
        query.center!.latitude,
        query.center!.longitude,
        file.location.latitude,
        file.location.longitude
      ),
    }));
  }

  // Bounding box filter
  if (query.bounds) {
    searchResults = filterByBounds(searchResults, query.bounds) as WeatherFileSearchResult[];
  }

  // Elevation filter
  if (query.minElevation !== undefined || query.maxElevation !== undefined) {
    searchResults = filterByElevation(
      searchResults,
      query.minElevation,
      query.maxElevation
    ) as WeatherFileSearchResult[];
  }

  // Sort results
  const sortBy = query.sortBy ?? (query.center ? 'distance' : 'popularity');
  const sortOrder = query.sortOrder ?? (sortBy === 'popularity' ? 'desc' : 'asc');
  searchResults = sortResults(searchResults, sortBy, sortOrder);

  // Get total count before pagination
  const totalCount = searchResults.length;

  // Pagination
  const offset = query.offset ?? 0;
  const limit = query.limit ?? 50;
  const paginatedResults = searchResults.slice(offset, offset + limit);
  const hasMore = offset + paginatedResults.length < totalCount;

  return {
    results: paginatedResults,
    totalCount,
    hasMore,
  };
}

/**
 * Calculate additive facet counts based on currently applied filters.
 * For each filter category, counts are calculated with OTHER filters applied
 * (but not the current category), showing what would happen if each option was selected.
 */
function calculateAdditiveFacetCounts(
  baseFiles: WeatherFileMetadata[],
  query: WeatherFileSearchQuery
): FilterFacetCounts {
  // For climate zones: apply source type and year range filters, count by zone
  let filesForClimateZones = baseFiles;
  if (query.sourceType) {
    filesForClimateZones = filterBySourceType(filesForClimateZones, query.sourceType);
  }
  if (query.yearRange) {
    filesForClimateZones = filterByYearRange(filesForClimateZones, query.yearRange);
  }

  // For source types: apply climate zone and year range filters, count by type
  let filesForSourceTypes = baseFiles;
  if (query.climateZone) {
    filesForSourceTypes = filterByClimateZone(filesForSourceTypes, query.climateZone);
  }
  if (query.yearRange) {
    filesForSourceTypes = filterByYearRange(filesForSourceTypes, query.yearRange);
  }

  // For year ranges: apply climate zone and source type filters, count by range
  let filesForYearRanges = baseFiles;
  if (query.climateZone) {
    filesForYearRanges = filterByClimateZone(filesForYearRanges, query.climateZone);
  }
  if (query.sourceType) {
    filesForYearRanges = filterBySourceType(filesForYearRanges, query.sourceType);
  }

  // Calculate counts for each filter category from its filtered set
  const climateZoneMap = new Map<string, number>();
  for (const file of filesForClimateZones) {
    const zone = file.climateZone.ashrae;
    if (zone) {
      climateZoneMap.set(zone, (climateZoneMap.get(zone) ?? 0) + 1);
    }
  }

  const sourceTypeMap = new Map<string, number>();
  for (const file of filesForSourceTypes) {
    const type = file.source.type;
    sourceTypeMap.set(type, (sourceTypeMap.get(type) ?? 0) + 1);
  }

  const yearRangeMap = new Map<string, number>();
  for (const file of filesForYearRanges) {
    const years = file.source.years || 'none';
    yearRangeMap.set(years, (yearRangeMap.get(years) ?? 0) + 1);
  }

  return {
    climateZones: Array.from(climateZoneMap.entries())
      .map(([zone, count]) => ({ zone, count }))
      .sort((a, b) => a.zone.localeCompare(b.zone)),
    sourceTypes: Array.from(sourceTypeMap.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count),
    yearRanges: Array.from(yearRangeMap.entries())
      .map(([yearRange, count]) => ({ yearRange, count }))
      .sort((a, b) => {
        if (a.yearRange === 'none') return 1;
        if (b.yearRange === 'none') return -1;
        const aYear = parseInt(a.yearRange.split('-')[0] || '0');
        const bYear = parseInt(b.yearRange.split('-')[0] || '0');
        return bYear - aYear;
      }),
  };
}

/**
 * Search weather files and return results with total count and facet counts.
 * Facet counts are calculated based on the base filtered set (text search + country),
 * allowing dynamic filter count updates as the user selects filters.
 */
export async function searchWeatherFilesWithFacets(
  query: WeatherFileSearchQuery,
  loadedFiles?: WeatherFileMetadata[]
): Promise<SearchResultWithFacets> {
  let files: WeatherFileMetadata[];

  if (loadedFiles) {
    files = loadedFiles;
  } else if (query.countryCode) {
    files = await loadCountryWeatherFiles(query.countryCode);
  } else {
    const manifest = await loadWeatherManifest();
    files = [];
    const loadPromises = manifest.countries.map((c) => loadCountryWeatherFiles(c.code));
    const settledResults = await Promise.allSettled(loadPromises);
    for (const result of settledResults) {
      if (result.status === 'fulfilled') {
        files.push(...result.value);
      }
    }
  }

  let baseFilteredFiles: WeatherFileMetadata[] = files;

  // Apply text search first (this is the base for facet calculation)
  if (query.text && query.text.trim().length > 0) {
    baseFilteredFiles = textSearch(query.text, baseFilteredFiles);
  }

  // Apply country filter if using loaded files
  if (query.countryCode && loadedFiles) {
    baseFilteredFiles = filterByCountry(baseFilteredFiles, query.countryCode);
  }

  // Calculate additive facets: for each filter category, counts reflect
  // how many results would match if that option was added to current selection
  const facets = calculateAdditiveFacetCounts(baseFilteredFiles, query);

  // Now apply additional filters for the results
  let results: WeatherFileSearchResult[] = baseFilteredFiles.map((f) => ({ ...f }));

  // Climate zone filter
  if (query.climateZone) {
    results = filterByClimateZone(results, query.climateZone) as WeatherFileSearchResult[];
  }

  // Source type filter
  if (query.sourceType) {
    results = filterBySourceType(results, query.sourceType) as WeatherFileSearchResult[];
  }

  // Year range filter
  if (query.yearRange) {
    results = filterByYearRange(results, query.yearRange) as WeatherFileSearchResult[];
  }

  // Geographic radius filter
  if (query.center && query.radiusKm) {
    results = filterByRadius(
      results,
      query.center.latitude,
      query.center.longitude,
      query.radiusKm
    );
  } else if (query.center) {
    results = results.map((file) => ({
      ...file,
      distance: calculateDistance(
        query.center!.latitude,
        query.center!.longitude,
        file.location.latitude,
        file.location.longitude
      ),
    }));
  }

  // Bounding box filter
  if (query.bounds) {
    results = filterByBounds(results, query.bounds) as WeatherFileSearchResult[];
  }

  // Elevation filter
  if (query.minElevation !== undefined || query.maxElevation !== undefined) {
    results = filterByElevation(
      results,
      query.minElevation,
      query.maxElevation
    ) as WeatherFileSearchResult[];
  }

  // Sort results
  const sortBy = query.sortBy ?? (query.center ? 'distance' : 'popularity');
  const sortOrder = query.sortOrder ?? (sortBy === 'popularity' ? 'desc' : 'asc');
  results = sortResults(results, sortBy, sortOrder);

  // Get total count before pagination
  const totalCount = results.length;

  // Pagination
  const offset = query.offset ?? 0;
  const limit = query.limit ?? 50;
  const paginatedResults = results.slice(offset, offset + limit);
  const hasMore = offset + paginatedResults.length < totalCount;

  return {
    results: paginatedResults,
    totalCount,
    hasMore,
    facets,
  };
}

/**
 * Get weather file by ID.
 * This requires loading all countries to find the file.
 */
export async function getWeatherFileById(id: string): Promise<WeatherFileMetadata | undefined> {
  // Extract country code from ID if possible (e.g., "us-co-denver-intl" -> "US")
  const match = id.match(/^([a-z]{2})-/i);
  if (match && match[1]) {
    const countryCode = match[1].toUpperCase();
    const files = await loadCountryWeatherFiles(countryCode);
    return files.find((f) => f.id === id);
  }

  // Fallback: search all countries
  const manifest = await loadWeatherManifest();
  for (const country of manifest.countries) {
    const files = await loadCountryWeatherFiles(country.code);
    const found = files.find((f) => f.id === id);
    if (found) return found;
  }

  return undefined;
}

/**
 * Get all unique country codes from the manifest (async version).
 */
export async function getAvailableCountries(): Promise<
  { code: string; name: string; count: number }[]
> {
  const manifest = await loadWeatherManifest();
  return manifest.countries;
}

/**
 * Get the manifest directly for components that need it.
 */
export async function getWeatherManifest(): Promise<WeatherIndexManifest> {
  return loadWeatherManifest();
}

/**
 * Get available climate zones with counts.
 * Note: This requires loading all data.
 */
export async function getAvailableClimateZones(
  loadedFiles?: WeatherFileMetadata[]
): Promise<{ zone: ASHRAEClimateZone; count: number }[]> {
  let files = loadedFiles;
  if (!files) {
    const manifest = await loadWeatherManifest();
    files = [];
    for (const country of manifest.countries) {
      const cf = await loadCountryWeatherFiles(country.code);
      files.push(...cf);
    }
  }

  const zoneMap = new Map<ASHRAEClimateZone, number>();

  for (const file of files) {
    const zone = file.climateZone.ashrae;
    if (zone) {
      zoneMap.set(zone, (zoneMap.get(zone) ?? 0) + 1);
    }
  }

  return Array.from(zoneMap.entries())
    .map(([zone, count]) => ({ zone, count }))
    .sort((a, b) => a.zone.localeCompare(b.zone));
}

/**
 * Get available source types with counts.
 * Note: This requires loading all data.
 */
export async function getAvailableSourceTypes(
  loadedFiles?: WeatherFileMetadata[]
): Promise<{ type: WeatherFileSourceType; count: number }[]> {
  let files = loadedFiles;
  if (!files) {
    const manifest = await loadWeatherManifest();
    files = [];
    for (const country of manifest.countries) {
      const cf = await loadCountryWeatherFiles(country.code);
      files.push(...cf);
    }
  }

  const typeMap = new Map<WeatherFileSourceType, number>();

  for (const file of files) {
    const type = file.source.type;
    typeMap.set(type, (typeMap.get(type) ?? 0) + 1);
  }

  return Array.from(typeMap.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Get available year ranges with counts.
 */
export async function getAvailableYearRanges(
  loadedFiles?: WeatherFileMetadata[]
): Promise<{ yearRange: string; count: number }[]> {
  let files = loadedFiles;
  if (!files) {
    const manifest = await loadWeatherManifest();
    files = [];
    for (const country of manifest.countries) {
      const cf = await loadCountryWeatherFiles(country.code);
      files.push(...cf);
    }
  }

  const rangeMap = new Map<string, number>();

  for (const file of files) {
    const years = file.source.years || 'none';
    rangeMap.set(years, (rangeMap.get(years) ?? 0) + 1);
  }

  return Array.from(rangeMap.entries())
    .map(([yearRange, count]) => ({ yearRange, count }))
    .sort((a, b) => {
      // Sort by start year descending (most recent first), 'none' at the end
      if (a.yearRange === 'none') return 1;
      if (b.yearRange === 'none') return -1;
      const aYear = parseInt(a.yearRange.split('-')[0] || '0');
      const bYear = parseInt(b.yearRange.split('-')[0] || '0');
      return bYear - aYear;
    });
}

/**
 * Get nearby weather files from a location.
 * Note: This requires loading all data for accurate results.
 */
export async function getNearbyWeatherFiles(
  latitude: number,
  longitude: number,
  limit: number = 10
): Promise<WeatherFileSearchResult[]> {
  return searchWeatherFiles({
    center: { latitude, longitude },
    sortBy: 'distance',
    sortOrder: 'asc',
    limit,
  });
}

/**
 * Get popular weather files.
 * Note: This requires loading all data.
 */
export async function getPopularWeatherFiles(
  limit: number = 20
): Promise<WeatherFileMetadata[]> {
  const manifest = await loadWeatherManifest();
  const files: WeatherFileMetadata[] = [];

  for (const country of manifest.countries) {
    const cf = await loadCountryWeatherFiles(country.code);
    files.push(...cf);
  }

  return files
    .sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0))
    .slice(0, limit);
}

/**
 * Get weather files by climate zone.
 */
export async function getWeatherFilesByClimateZone(
  zone: ASHRAEClimateZone,
  limit?: number
): Promise<WeatherFileMetadata[]> {
  const manifest = await loadWeatherManifest();
  const results: WeatherFileMetadata[] = [];

  for (const country of manifest.countries) {
    const files = await loadCountryWeatherFiles(country.code);
    const filtered = filterByClimateZone(files, zone);
    results.push(...filtered);
  }

  return limit ? results.slice(0, limit) : results;
}

/**
 * Get the total count of weather files from manifest.
 */
export async function getWeatherFileCount(): Promise<number> {
  const manifest = await loadWeatherManifest();
  return manifest.totalEntries;
}
